<template>
    <div id="contents" class="contents spirit-of-japan">
        <section class="main">
            <div class="texts">
                <div class="inner">
                    <p class="sub">A Journey to the True Spirit of Japan</p>
                    <h2>Unlocking the Secrets of the Shogun, Samurai and Ninja</h2>
                    <p class="lead">
                        Japan’s Warring States Period (1467 – 1568) was a golden age of the samurai as warlords and shogun (generals) crossed swords to seize power and glory. And while battles for supremacy raged across Japan, ninja operated in secret and well-educated pirates emerged to police local waters.<br>
                        The deeds of these military leaders and their “heroes in the shadows” are a quintessential part of Japan’s history and culture. Come and explore areas where key events unfolded and the spirit of those times still lingers.
                    </p>
                </div>
            </div>
            <div class="img"><img src="../../assets/spirit_of_japan3/main01.png" alt="" class="_pc"><img src="../../assets/spirit_of_japan3/main01_sp.png" alt="" class="_sp"></div>
        </section>
        <section class="basic-section">
            <div class="para -reverse">
                <div class="img"><img src="../../assets/spirit_of_japan3/img01.png" alt=""></div>
                <div class="txt">
                    <h3>The Warlord Who Conquered Guests’ Hearts</h3>
                    <p>
                        Oda Nobunaga (1534 – 1582) was a powerful feudal warlord who sought to unify the nation from his stronghold in what is now Gifu City in central Japan. Despite his reputation for being ruthless on the battlefield, Nobunaga also was an expert at entertaining guests, from other areas of Japan and from other countries. He built a luxurious palace described as “paradise on Earth” near his castle, which itself boasted a magnificent view. Nobunaga often delighted guests with displays of a fishing technique called <i>ukai</i> that used trained cormorants on the nearby Nagara River.<br>
                        The palace no longer exists, but cultural traditions that took shape at the castle, town, and river during Nobunaga’s time in power live on. The culture of entertaining guests still impresses and wins over visitors today.
                    </p>
                    <div class="btns">
                        <ul>
                            <li>
                                <router-link :to="{name: 'Popular', params: {id: 'b6e80626-8b49-4d11-b42f-ab86b993beab', page: 'about', url: 'ruthless-warlord\'s-spirit-hospitality-gifu-oda-nobunaga'}}" class="-black">
                                    <span>Read More</span>
                                </router-link>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </section>
        <section class="basic-section">
            <div class="para">
                <div class="img"><img src="../../assets/spirit_of_japan3/img02.png" alt=""></div>
                <div class="txt">
                    <h3>Where Ninja History Comes out of the Shadows</h3>
                    <p>
                        Ninja are widely known as covert warriors who gathered intelligence about enemy movements and conducted unconventional combat techniques. Despite this, the origins of ninja and who they really were often remain as hidden as their secret missions.<br>
                        Many answers to questions about ninja history come out of the shadows for all to see in Iga City, Mie Prefecture, and neighboring Koka City, Shiga Prefecture, two areas jointly considered to be the birthplace of ninja arts. Museums, temples, castles, and theme parks in these cities offer insights into ninja training, both physical and mental. You can throw <i>shuriken</i> stars, scale walls, and explore houses with contraptions designed to fool intruders to see how your skills match up against those of a trained ninja.
                    </p>
                    <div class="btns">
                        <ul>
                            <li>
                                <router-link :to="{name: 'Popular', params: {id: '4b55d9ab-7f54-4f81-ba78-08e05385b65e', page: 'about', url:'iga-koka-birthplace-ninja-shiga-mie-sengoku-period-shuriken'}}" class="-black">
                                    <span>Read More</span>
                                </router-link>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </section>
        <section class="basic-section">
            <div class="para -reverse">
                <div class="img"><img src="../../assets/spirit_of_japan3/img03.png" alt=""></div>
                <div class="txt">
                    <h3>The Educated “Pirates” with a Conscience</h3>
                    <p>
                        The Setonaikai Sea between the large islands of Honshu, Shikoku, and Kyushu is Japan’s largest inland sea and is dotted with about 700 islands. The <i>Murakami Kaizoku</i> were pirates who controlled and maintained order on this important trade route from the 14th to 16th centuries. These skilled navigators guaranteed safe passage through the perilous waters and were men of culture, highly educated, and devout in their beliefs. In many ways, these pirates were more like maritime police than the sea bandits of the West.<br>
                        Traces of their domain survive in the cities of Imabari in Ehime Prefecture and Onomichi in Hiroshima Prefecture, and the Geiyo Islands in the sea’s western reaches. These include poetry, dances, and pots used for cooking seafood dishes.
                    </p>
                    <div class="btns">
                        <ul>
                            <li>
                                <router-link :to="{name: 'Popular', params: {id: '030f3fe8-6696-4cad-9b7f-7f50312583af', page: 'about', url: 'murakami-kaizoku-geiyo-pirates-setonaikai-ehime-hiroshima'}}" class="-black">
                                    <span>Read More</span>
                                </router-link>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </section>
        <section class="basic-section">
            <div class="para">
                <div class="img"><img src="../../assets/spirit_of_japan3/img04.png" alt=""></div>
                <div class="txt">
                    <h3>A City Culture Built on a Samurai’s Sense of Refinement</h3>
                    <p>
                        Date Masamune (1567 – 1636) was a highly cultured feudal warlord skilled in the political and military arenas. He spearheaded the development of today’s Sendai City, Miyagi Prefecture, where his power base was located, and was intent on making his fiefdom as culturally rich as the distant capital.<br>
                        Masamune’s aesthetic sense was evident in his distinctive helmet that bore a crescent-shaped decoration, and the exquisite golden paintings on the walls inside Zuiganji Temple, which was built under his instruction and completed in 1609. The temple’s main hall and living quarter have been designated as National Treasures.<br>
                        Masamune’s affinity for a discerning life was inherited by his successors, and his influence lives on in Sendai’s unique traditional culture and crafts.
                    </p>
                    <div class="btns">
                        <ul>
                            <li>
                                <router-link :to="{name: 'Popular', params: {id: 'ae906e0e-7d7f-467c-8dd2-b3edcfe6782d', page: 'about', url: 'sendai-home-sophisticated-samurai-miyagi-date-masamune'}}" class="-black">
                                    <span>Read More</span>
                                </router-link>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </section>
        <section class="basic-section">
            <div class="para -reverse">
                <div class="img"><img src="../../assets/spirit_of_japan3/img05.png" alt=""></div>
                <div class="txt">
                    <h3>Samurai Villages that Shaped Japan’s Modern Transition</h3>
                    <p>
                        When Japan ended its national isolation policy in the latter half of the 19th century, many of the samurai who played key roles in the nation’s transition to a modern state came from the Satsuma clan in southern Kyushu.<br>
                        This large clan sub-divided its domain into dozens of small villages called <i>fumoto</i>, which were surrounded by hedges and stone walls to protect them from intruders. Outdoor spaces were used for practicing martial arts, archery, and shooting guns, and the children’s education trained both body and mind. Many <i>fumoto</i> where these samurai grew up have been preserved. Visitors can still see the streets and residences there, and get a sense of the culture that shaped the samurai who led Japan through a critical period in its history.
                    </p>
                    <div class="btns">
                        <ul>
                            <li>
                                <router-link :to="{name: 'Popular', params: {id: 'd87f327a-98a1-45d2-82bf-9b57824b5410', page: 'about', url: 'martial-towns-satsuma-samurai-kagoshima'}}" class="-black">
                                    <span>Read More</span>
                                </router-link>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
    </section>
    <section class="section03">
            <div class="inner">
                <h2>Other Articles</h2>
                <ul>
                    <li>
                        <router-link :to="{name: 'SpiritOfJapan1'}">
                            <div class="img"><img src="../../assets/spirit_of_japan/other.jpg" alt=""></div>
                            <div class="text">
                                <div class="headline">Spirit of Japan</div>
                                <div class="title">Discover Craftsmanship Unbowed by Time</div>
                            </div>
                        </router-link>
                    </li>
                    <li>
                        <router-link :to="{name: 'SpiritOfJapan2'}">
                            <div class="img"><img src="../../assets/spirit_of_japan2/other.jpg" alt=""></div>
                            <div class="text">
                                <div class="headline">Spirit of Japan</div>
                                <div class="title">Sacred Sites where Nature and Faith Blend into One</div>
                            </div>
                        </router-link>
                    </li>
                    <li>
                        <router-link :to="{name: 'SpiritOfJapan4'}">
                            <div class="img"><img src="../../assets/spirit_of_japan4/other.webp" alt=""></div>
                            <div class="text">
                                <div class="headline">Spirit of Japan</div>
                                <div class="title">The Journey of Japan's Original Landscapes Shaped by Water</div>
                            </div>
                        </router-link>
                    </li>
                </ul>
            </div>
        </section>
    </div>
</template>

<script>
import ApiRouter from "../../components/Systems/ApiRouter";

export default {
    name: "PlanYourVisit",
    mixins: [ApiRouter],
    data: function () {
        return {
            articles: [],
            activeTabMenu: 'half'
        }
    },
    created: function () {
        this.loadData();
        this.getQuery();
        document.title = 'Spirit Of Japan | Japan Heritage Official Site'
    },
    methods: {
        loadData: function () {
            window.axios.get(this.routes.articles)
                .then((response) => {
                    this.articles = response.data.data;
                });
        },
        getQuery: function () {
            const tab = this.$route.query.tab;

            switch (tab) {
                case 'day1':
                    this.activeTabMenu = 'day1';
                    break;
                case 'day2':
                    this.activeTabMenu = 'day2';
                    break;
                case 'day3':
                    this.activeTabMenu = 'day3';
                    break;
                default:
                    this.activeTabMenu = 'half';
                    break;
            }
        },
        articleCardHasClicked: function (id, hash, breadcrumb) {
            this.$router.push({name: 'Popular', params: {id: id, page: 'travelInfo', breadcrumb: breadcrumb}, hash: hash});
        }
    },
    watch: {
        $route() {
            this.getQuery();
        }
    }
}
</script>

<style src="./SpiritOfJapan3.scss" lang="scss" scoped />
